/**
 * 自由变换的手柄数学（M2-14 / DESIGN.md §5.3）。全部纯函数、表驱动可测。
 *
 * 手柄布局（文档空间的图层外接矩形）：
 * ```
 * nw── n ──ne
 * │         │      rotate：手柄圈外
 * w         e      skew：按住 Ctrl 拖边
 * │         │
 * sw── s ──se
 * ```
 * 拖动语义照 Photoshop：角=双轴缩放（Shift 等比）、边=单轴、
 * 锚点默认是对角/对边（Alt 换中心）、rotate 绕锚点转。
 */
import { aboutAnchor, rotation, scaling, shearing, type Mat3 } from './mat3';

/** 矩形（与文档层 Rect 结构等价）。 */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type ScaleHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export type HandleId = ScaleHandle | 'rotate';

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** 各手柄在矩形上的位置（0–1 归一化）。 */
const HANDLE_POSITION: Readonly<Record<ScaleHandle, readonly [number, number]>> = {
  nw: [0, 0],
  n: [0.5, 0],
  ne: [1, 0],
  e: [1, 0.5],
  se: [1, 1],
  s: [0.5, 1],
  sw: [0, 1],
  w: [0, 0.5],
};

/** 手柄的文档空间坐标。 */
export function handlePoint(bounds: Rect, handle: ScaleHandle): Point {
  const [u, v] = HANDLE_POSITION[handle];
  return { x: bounds.x + bounds.width * u, y: bounds.y + bounds.height * v };
}

/** 缺省锚点：对角/对边（Photoshop 语义）。 */
export function anchorFor(bounds: Rect, handle: ScaleHandle): Point {
  const [u, v] = HANDLE_POSITION[handle];
  return { x: bounds.x + bounds.width * (1 - u), y: bounds.y + bounds.height * (1 - v) };
}

export interface ScaleOptions {
  /** Shift：等比。 */
  readonly uniform?: boolean;
  /** Alt：以中心为锚。 */
  readonly fromCenter?: boolean;
}

/** 拖缩放手柄 → 变换矩阵（绕锚点）。 */
export function scaleByHandle(
  bounds: Rect,
  handle: ScaleHandle,
  to: Point,
  options: ScaleOptions = {},
): Mat3 {
  const anchor =
    options.fromCenter === true
      ? { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 }
      : anchorFor(bounds, handle);
  const from = handlePoint(bounds, handle);

  const [u, v] = HANDLE_POSITION[handle];
  const affectsX = u !== 0.5;
  const affectsY = v !== 0.5;

  const denomX = from.x - anchor.x;
  const denomY = from.y - anchor.y;
  let sx = affectsX && denomX !== 0 ? (to.x - anchor.x) / denomX : 1;
  let sy = affectsY && denomY !== 0 ? (to.y - anchor.y) / denomY : 1;

  if (options.uniform === true && affectsX && affectsY) {
    // 等比：取绝对值更大的那个轴（跟手），符号各自保留（允许镜像）
    const magnitude = Math.max(Math.abs(sx), Math.abs(sy));
    sx = Math.sign(sx || 1) * magnitude;
    sy = Math.sign(sy || 1) * magnitude;
  }
  return aboutAnchor(scaling(sx, sy), anchor);
}

/**
 * 拖任意一个手柄（角或边）都产出等比缩放——"置入图片"场景专用
 * （DESIGN.md §2.6 补充：拖放导入按 PS 惯例只支持等比缩放，不像自由变换
 * 那样默认自由缩放、Shift 才等比）。
 *
 * 跟 {@link scaleByHandle} 的 `uniform` 选项不同：那个选项只在角手柄上生效
 * （`affectsX && affectsY` 都要求两轴都受该手柄影响，边手柄天生只影响一轴，
 * 加了 uniform 也白搭）。这里改用"锚点到手柄的距离"当缩放比例——
 * 角手柄退化成跟 scaleByHandle 的 uniform 选项完全一样（对角线距离比），
 * 边手柄则是"沿边法线方向拖多远"当统一缩放比例，两种手柄手感一致，
 * 不需要为"用户抓的是角还是边"分岔处理。
 */
export function uniformScaleByHandle(bounds: Rect, handle: ScaleHandle, to: Point): Mat3 {
  const anchor = anchorFor(bounds, handle);
  const from = handlePoint(bounds, handle);
  const fromDist = Math.hypot(from.x - anchor.x, from.y - anchor.y);
  const toDist = Math.hypot(to.x - anchor.x, to.y - anchor.y);
  const scale = fromDist === 0 ? 1 : toDist / fromDist;
  return aboutAnchor(scaling(scale, scale), anchor);
}

/** 旋转：from→to 相对锚点的角度差。 */
export function rotateByHandle(anchor: Point, from: Point, to: Point): Mat3 {
  const a0 = Math.atan2(from.y - anchor.y, from.x - anchor.x);
  const a1 = Math.atan2(to.y - anchor.y, to.x - anchor.x);
  return aboutAnchor(rotation(a1 - a0), anchor);
}

/** 斜切：拖水平边产生 x 随 y 的错切，拖竖直边产生 y 随 x 的错切。 */
export function skewByHandle(
  bounds: Rect,
  handle: 'n' | 's' | 'e' | 'w',
  from: Point,
  to: Point,
): Mat3 {
  const anchor = anchorFor(bounds, handle);
  if (handle === 'n' || handle === 's') {
    const height = handlePoint(bounds, handle).y - anchor.y;
    const shx = height === 0 ? 0 : (to.x - from.x) / height;
    return aboutAnchor(shearing(shx, 0), anchor);
  }
  const width = handlePoint(bounds, handle).x - anchor.x;
  const shy = width === 0 ? 0 : (to.y - from.y) / width;
  return aboutAnchor(shearing(0, shy), anchor);
}

/** 命中测试：文档坐标 point 落在哪个手柄上（tolerance 为文档像素）。 */
export function hitTestHandles(
  bounds: Rect,
  point: Point,
  tolerance: number,
): HandleId | null {
  const handles: ScaleHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
  for (const handle of handles) {
    const p = handlePoint(bounds, handle);
    if (Math.abs(point.x - p.x) <= tolerance && Math.abs(point.y - p.y) <= tolerance) {
      return handle;
    }
  }
  // 手柄圈外一段距离视为旋转
  const outside =
    point.x < bounds.x - tolerance ||
    point.x > bounds.x + bounds.width + tolerance ||
    point.y < bounds.y - tolerance ||
    point.y > bounds.y + bounds.height + tolerance;
  if (outside) {
    const near =
      point.x > bounds.x - tolerance * 4 &&
      point.x < bounds.x + bounds.width + tolerance * 4 &&
      point.y > bounds.y - tolerance * 4 &&
      point.y < bounds.y + bounds.height + tolerance * 4;
    if (near) return 'rotate';
  }
  return null;
}
